import React, { PropsWithChildren } from "react";
import { Colour } from "../../theme/theme";
import Button, { ButtonVariant } from "../button/Button";
import Modal, { ModalDisplayProps, ModalProps } from "../modal/Modal";
import { alignEnd, flexRow } from "../../reusableStyles/flex";
import { gap } from "../../reusableStyles/gap";
import Typography from "../typography/Typography";
import { useTheme } from "@emotion/react";

export interface DialogAction {
  text: string;
  action: () => void;
  color?: Colour;
  variant?: ButtonVariant;
  grey?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
}
export interface TextDialogWithActionsProps
  extends ModalProps,
    PropsWithChildren {
  actions: DialogAction[];
  title: string;
  subtitle?: string;
}

const TextDialogWithActions: React.FC<TextDialogWithActionsProps> = (props) => {
  const theme = useTheme();
  return (
    <Modal
      withBackground={props.withBackground}
      preventScroll={props.preventScroll}
      isOpen={props.isOpen}
      onClose={props.onClose}
      preventClose={props.preventClose}
    >
      <Typography text={props.title} size="lg" weight="semibold" />
      {props.subtitle && (
        <Typography
          text={props.subtitle}
          color={theme.colours.grey["600"]}
          size="md"
          weight="regular"
        />
      )}
      {props.children}
      {props.actions.length > 0 && (
        <div css={[flexRow, alignEnd, gap(12)]}>
          {props.actions.map((action, index) => (
            <Button
              key={action.text + index}
              text={action.text}
              color={action.color}
              variant={action.variant}
              size="lg"
              onClick={action.action}
              grey={action.grey}
              fullWidth={action.fullWidth}
              disabled={action.disabled}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export interface ConfirmDialogProps
  extends PropsWithChildren,
    ModalDisplayProps {
  title: string;
  onConfirm: () => void;
  onCancel: () => void;
  disabled?: boolean;
  subtitle?: string;
  destructive?: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = (props) => {
  return (
    <TextDialogWithActions
      isOpen={props.isOpen}
      onClose={props.onCancel}
      title={props.title}
      subtitle={props.subtitle}
      actions={[
        {
          text: "Cancel",
          action: props.onCancel,
          variant: "secondary",
          grey: true,
        },
        {
          text: "Confirm",
          action: props.onConfirm,
          color: props.destructive ? "error" : "primary",
          disabled: props.disabled ?? false,
        },
      ]}
      withBackground={props.withBackground}
      preventScroll={props.preventScroll}
      preventClose={props.preventClose}
    >
      {props.children}
    </TextDialogWithActions>
  );
};

export interface AlertDialogProps extends PropsWithChildren, ModalDisplayProps {
  title: string;
  onConfirm: () => void;
  subtitle?: string;
  disabled?: boolean;
  confirmText?: string;
}

const AlertDialog: React.FC<AlertDialogProps> = (props) => {
  return (
    <TextDialogWithActions
      isOpen={props.isOpen}
      onClose={props.onConfirm}
      title={props.title}
      subtitle={props.subtitle}
      actions={[
        {
          text: props.confirmText ?? "Okay",
          action: props.onConfirm,
          fullWidth: true,
          disabled: props.disabled ?? false,
        },
      ]}
      withBackground={props.withBackground}
      preventScroll={props.preventScroll}
      preventClose={props.preventClose}
    >
      {props.children}
    </TextDialogWithActions>
  );
};

export { TextDialogWithActions, ConfirmDialog, AlertDialog };
